import type { PersonMonthRow, UsageTreeRow } from "../api/types";
import { minutesLabel, tilesLabel } from "./format";

function PeopleTable({ people }: { people: PersonMonthRow[] }): JSX.Element {
  const cell = { padding: "3px 10px 3px 0", borderBottom: "1px solid #eee" };
  const num = { ...cell, textAlign: "right" as const };
  if (people.length === 0) {
    return <p style={{ margin: 0, color: "#666" }}>Nothing yet this month.</p>;
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr>
            <th style={{ ...cell, textAlign: "left" }}>Name</th>
            <th style={{ ...cell, textAlign: "left" }}>Team</th>
            <th style={num}>Tiles</th>
            <th style={num}>Active</th>
            <th style={num}>Most devices</th>
            <th style={num}>Boots</th>
          </tr>
        </thead>
        <tbody>
          {people.map((p) => (
            <tr key={`${p.team}/${p.reviewerId}`}>
              <td style={cell}>{p.displayName ?? "(no name)"}</td>
              <td style={cell}>{p.team}</td>
              <td style={num}>{tilesLabel(p.tiles)}</td>
              <td style={num}>{minutesLabel(p.activeMinutes)}</td>
              <td style={num}>{p.maxDevices}</td>
              <td style={num}>{p.boots}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Spec §9/§16: admin gets every tree (by tiles) with people under each;
 *  a parent gets its own tree's people. `failed` (Task 6/7's
 *  `UsageTreesResponse.failed`) names root slugs whose presence object
 *  couldn't be read — partial data beats none, so the rest still renders. */
export function TopSpenders({
  trees,
  people,
  failed = [],
}: {
  trees: UsageTreeRow[] | null;
  people: PersonMonthRow[] | null;
  failed?: string[];
}): JSX.Element {
  return (
    <section style={{ marginBottom: 24 }}>
      <h2 style={{ fontSize: 18, margin: "0 0 8px" }}>Who spent the most</h2>
      {failed.length > 0 && (
        <p style={{ margin: "0 0 8px", color: "#b91c1c" }}>
          Couldn't read usage for {failed.join(", ")}.
        </p>
      )}
      {trees !== null ? (
        trees.length === 0 ? (
          <p style={{ margin: 0, color: "#666" }}>Nothing yet this month.</p>
        ) : (
          trees.map((t) => (
            <section key={t.root} style={{ marginBottom: 12 }}>
              <h3 style={{ fontSize: 15, margin: "0 0 4px" }}>
                {t.displayName}{" "}
                <span style={{ color: "#666", fontWeight: 400 }}>
                  · {tilesLabel(t.tiles)} tiles
                </span>
              </h3>
              <PeopleTable people={t.people} />
            </section>
          ))
        )
      ) : (
        <PeopleTable people={people ?? []} />
      )}
    </section>
  );
}
